import {
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import type { CommissionRule, CreateCommissionRuleInput, Business } from '@/types';
import { createLogger } from '@/utils/debug';
import { commissionRulesCollection } from '@/config/collections';

const log = createLogger('CommissionRules');

// ===========================================
// Commission Rules Collection Reference
// ===========================================

const rulesRef = commissionRulesCollection(db);

// ===========================================
// Helper: Convert Firestore document to CommissionRule
// ===========================================

function docToRule(doc: any): CommissionRule {
  const data = doc.data();
  return {
    id: doc.id,
    business: data.business as Business,
    projectType: data.projectType,
    commissionType: data.commissionType,
    commissionValue: data.commissionValue,
    commissionCap: data.commissionCap,
    description: data.description,
    isActive: data.isActive ?? true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  };
}

// ===========================================
// Commission Rule CRUD Operations
// ===========================================

/**
 * Get all commission rules
 */
export async function getCommissionRules(): Promise<CommissionRule[]> {
  return log.operation('getCommissionRules', async () => {
    const q = query(rulesRef, orderBy('business', 'asc'), orderBy('projectType', 'asc'));
    const snapshot = await getDocs(q);
    const rules = snapshot.docs.map(docToRule);
    log.debug('getCommissionRules completed', { count: rules.length });
    return rules;
  });
}

/**
 * Get commission rules for a specific business
 */
export async function getCommissionRulesByBusiness(
  business: Business
): Promise<CommissionRule[]> {
  const q = query(
    rulesRef,
    where('business', '==', business),
    orderBy('projectType', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToRule);
}

/**
 * Get active commission rules for a business
 */
export async function getActiveRulesByBusiness(
  business: Business
): Promise<CommissionRule[]> {
  const q = query(
    rulesRef,
    where('business', '==', business),
    where('isActive', '==', true),
    orderBy('projectType', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToRule);
}

/**
 * Get a single commission rule by ID
 */
export async function getCommissionRule(id: string): Promise<CommissionRule | null> {
  return log.operation('getCommissionRule', async () => {
    const docRef = doc(rulesRef, id);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      log.debug('getCommissionRule not found', { id });
      return null;
    }

    log.debug('getCommissionRule found', { id });
    return docToRule(snapshot);
  }, { id });
}

/**
 * Find a matching rule for a business and project type
 */
export async function findMatchingRule(
  business: Business,
  projectType: string
): Promise<CommissionRule | null> {
  const q = query(
    rulesRef,
    where('business', '==', business),
    where('projectType', '==', projectType),
    where('isActive', '==', true)
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return docToRule(snapshot.docs[0]);
}

/**
 * Create a new commission rule
 */
export async function createCommissionRule(
  input: CreateCommissionRuleInput
): Promise<CommissionRule> {
  return log.operation('createCommissionRule', async () => {
    // Build rule doc, excluding undefined values (Firestore doesn't accept undefined)
    const ruleDoc: Record<string, unknown> = {
      business: input.business,
      projectType: input.projectType,
      commissionType: input.commissionType,
      commissionValue: input.commissionValue,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Only add optional fields if they have values
    if (input.commissionCap !== undefined && input.commissionCap !== null) {
      ruleDoc.commissionCap = input.commissionCap;
    }
    if (input.description !== undefined && input.description !== null && input.description !== '') {
      ruleDoc.description = input.description;
    }

    const docRef = await addDoc(rulesRef, ruleDoc);
    const created = await getDoc(docRef);
    const rule = docToRule(created);
    log.info('createCommissionRule success', { id: rule.id, business: rule.business, projectType: rule.projectType });
    return rule;
  }, { business: input.business, projectType: input.projectType });
}

/**
 * Update an existing commission rule
 */
export async function updateCommissionRule(
  id: string,
  updates: Partial<CreateCommissionRuleInput & { isActive: boolean }>
): Promise<void> {
  return log.operation('updateCommissionRule', async () => {
    const docRef = doc(rulesRef, id);

    // Filter out undefined values (Firestore doesn't accept undefined)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cleanUpdates: Record<string, any> = {
      updatedAt: serverTimestamp(),
    };

    for (const [key, value] of Object.entries(updates)) {
      if (value !== undefined) {
        cleanUpdates[key] = value;
      }
    }

    await updateDoc(docRef, cleanUpdates);
    log.info('updateCommissionRule success', { id, fields: Object.keys(cleanUpdates) });
  }, { id, fields: Object.keys(updates) });
}

/**
 * Toggle rule active status
 */
export async function toggleRuleActive(id: string, isActive: boolean): Promise<void> {
  await updateCommissionRule(id, { isActive });
}

/**
 * Delete a commission rule
 */
export async function deleteCommissionRule(id: string): Promise<void> {
  return log.operation('deleteCommissionRule', async () => {
    const docRef = doc(rulesRef, id);
    await deleteDoc(docRef);
    log.info('deleteCommissionRule success', { id });
  }, { id });
}

// ===========================================
// Commission Calculation Utilities
// ===========================================

/**
 * Calculate commission for a given revenue and rule
 */
export function calculateCommission(
  totalRevenue: number,
  rule: CommissionRule
): number {
  let commission: number;

  if (rule.commissionType === 'percentage') {
    commission = totalRevenue * rule.commissionValue;
  } else {
    commission = rule.commissionValue;
  }

  // Apply cap if configured
  if (rule.commissionCap && commission > rule.commissionCap) {
    commission = rule.commissionCap;
  }

  return Math.round(commission * 100) / 100;
}

/**
 * Format commission rule for display
 */
export function formatRuleDescription(rule: CommissionRule): string {
  if (rule.commissionType === 'percentage') {
    const pct = (rule.commissionValue * 100).toFixed(1);
    let desc = `${pct}%`;
    if (rule.commissionCap) {
      desc += ` (max $${rule.commissionCap.toLocaleString()})`;
    }
    return desc;
  } else {
    return `$${rule.commissionValue.toLocaleString()} flat`;
  }
}

/**
 * Get unique project types from existing rules
 */
export async function getProjectTypes(): Promise<string[]> {
  const rules = await getCommissionRules();
  const types = new Set(rules.map((r) => r.projectType));
  return Array.from(types).sort();
}

/**
 * Recompute derived commission amounts on existing synced projects using the
 * current rules/partner configs (server-side, admin/owner only). Use after
 * adding or fixing rules so already-synced jobs reflect the new numbers.
 */
export async function recalculateCommissions(
  business?: Business
): Promise<{ totalProjects: number; updated: number; unchanged: number; errors: number }> {
  const fn = httpsCallable(getFunctions(), 'crm_recalculateCommissions');
  const res = await fn({ business, dryRun: false });
  return res.data as { totalProjects: number; updated: number; unchanged: number; errors: number };
}
